import { useRef, useState, type DragEvent, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Paperclip, Trash2, Upload, Link2, ExternalLink, FileText, NotebookPen, Plus,
} from 'lucide-react'
import { Button, Dialog, ConfirmDialog, cn } from '../../design'
import {
  attachmentsKey,
  createLink,
  deleteAttachment,
  formatBytes,
  isImage,
  listAttachments,
  previewUrl,
  uploadAttachment,
  type Attachment,
  type ParentType,
} from '../../api/attachments'
import { ApiError } from '../../api/client'

export function useAttachments(parentType: ParentType, parentId: number | null) {
  return useQuery({
    queryKey: attachmentsKey(parentType, parentId),
    queryFn: () => listAttachments(parentType, parentId as number),
    enabled: parentId !== null,
    // Signed URLs expire (30 min), so don't serve stale ones from cache forever.
    staleTime: 5 * 60_000,
  })
}

/** Attachments and links for one story, as a drawer section. */
export function AttachmentsSection({ storyId }: { storyId: number }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<Attachment | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Attachment | null>(null)
  const [addingLink, setAddingLink] = useState(false)

  const q = useAttachments('story', storyId)
  const invalidate = () => qc.invalidateQueries({ queryKey: attachmentsKey('story', storyId) })

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadAttachment('story', storyId, file),
    onSuccess: () => { setError(null); invalidate() },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Upload failed'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteAttachment(id),
    onSuccess: invalidate,
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not delete'),
  })

  const items = q.data ?? []
  const images = items.filter(isImage)
  const files = items.filter(a => a.kind === 'file' && !isImage(a))
  const links = items.filter(a => a.kind === 'link')

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    for (const file of Array.from(e.dataTransfer.files)) uploadMut.mutate(file)
  }

  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-accent">
          Attachments{items.length > 0 && ` · ${items.length}`}
        </h3>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setAddingLink(v => !v)} leftIcon={<Link2 size={12} />}>
            Link
          </Button>
          <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()} leftIcon={<Plus size={12} />}>
            Add
          </Button>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        onChange={e => {
          for (const file of Array.from(e.target.files ?? [])) uploadMut.mutate(file)
          e.target.value = ''
        }}
      />

      {addingLink && (
        <AddLinkForm
          parentType="story"
          parentId={storyId}
          onDone={() => { setAddingLink(false); invalidate() }}
          onError={setError}
        />
      )}

      {error && (
        <p className="mb-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
          {error}
        </p>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'rounded-lg border-2 border-dashed p-2 transition-colors',
          dragging ? 'border-accent bg-accent/5' : 'border-transparent',
        )}
      >
        {items.length === 0 && !uploadMut.isPending && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border py-4 text-xs text-fg-subtle hover:border-accent hover:text-fg"
          >
            <Upload size={13} />
            Drop a file here, or click to choose
          </button>
        )}

        {images.length > 0 && (
          <div className="mb-2 grid grid-cols-4 gap-2">
            {images.map(a => (
              <div key={a.id} className="group relative aspect-square overflow-hidden rounded-md border border-border">
                <button type="button" onClick={() => setLightbox(a)} className="h-full w-full">
                  <img
                    src={previewUrl(a) ?? ''}
                    alt={a.file_name ?? ''}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${a.file_name}`}
                  onClick={() => setPendingDelete(a)}
                  className="absolute right-1 top-1 hidden rounded bg-black/60 p-1 text-white group-hover:block hover:bg-danger"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}

        {files.map(a => (
          <Row key={a.id} onDelete={() => setPendingDelete(a)}>
            <FileText size={14} className="shrink-0 text-fg-subtle" />
            <a
              href={a.content_url ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-accent hover:underline"
            >
              {a.file_name}
            </a>
            <span className="shrink-0 font-mono text-[10px] text-fg-subtle">{formatBytes(a.byte_size)}</span>
          </Row>
        ))}

        {links.map(a => (
          <Row key={a.id} onDelete={() => setPendingDelete(a)}>
            {a.provider === 'evernote'
              ? <NotebookPen size={14} className="shrink-0" style={{ color: '#3fa829' }} />
              : <Link2 size={14} className="shrink-0 text-fg-subtle" />}
            <a
              href={a.url ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-accent hover:underline"
            >
              {a.title || a.url}
            </a>
            <ExternalLink size={11} className="shrink-0 text-fg-subtle" />
          </Row>
        ))}

        {uploadMut.isPending && (
          <p className="px-2 py-1.5 text-xs text-fg-subtle">Uploading…</p>
        )}
      </div>

      <Dialog
        open={lightbox !== null}
        onOpenChange={open => !open && setLightbox(null)}
        title={lightbox?.file_name ?? 'Attachment'}
        size="full"
        bare
      >
        {lightbox && (
          <div className="flex h-full flex-col">
            <img
              src={lightbox.content_url ?? ''}
              alt={lightbox.file_name ?? ''}
              className="min-h-0 flex-1 object-contain"
            />
            <div className="flex items-center justify-between px-4 py-2 text-xs text-fg-subtle">
              <span className="truncate">{lightbox.file_name}</span>
              <span className="font-mono">
                {lightbox.width && lightbox.height ? `${lightbox.width}×${lightbox.height} · ` : ''}
                {formatBytes(lightbox.byte_size)}
              </span>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={open => !open && setPendingDelete(null)}
        title="Delete attachment?"
        description={pendingDelete?.file_name ?? pendingDelete?.title ?? undefined}
        onConfirm={() => pendingDelete && deleteMut.mutate(pendingDelete.id)}
      />
    </div>
  )
}

function Row({ children, onDelete }: { children: React.ReactNode; onDelete: () => void }) {
  return (
    <div className="group mb-1 flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm">
      {children}
      <button
        type="button"
        aria-label="Delete"
        onClick={onDelete}
        className="shrink-0 rounded p-1 text-fg-subtle opacity-0 group-hover:opacity-100 hover:text-danger"
      >
        <Trash2 size={12} />
      </button>
    </div>
  )
}

function AddLinkForm({
  parentType,
  parentId,
  onDone,
  onError,
}: {
  parentType: ParentType
  parentId: number
  onDone: () => void
  onError: (message: string) => void
}) {
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')

  const mut = useMutation({
    mutationFn: () => createLink(parentType, parentId, url.trim(), title.trim()),
    onSuccess: () => { setUrl(''); setTitle(''); onDone() },
    onError: (e: unknown) => onError(e instanceof ApiError ? e.message : 'Could not add the link'),
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!url.trim()) return
    mut.mutate()
  }

  return (
    <form onSubmit={submit} className="mb-2 flex gap-2 rounded-md border border-border bg-bg-elevated p-2">
      <input
        value={url}
        onChange={e => setUrl(e.target.value)}
        placeholder="https://www.evernote.com/…"
        autoFocus
        className="min-w-0 flex-[2] rounded border border-border bg-bg px-2 py-1 text-sm focus:border-accent focus:outline-none"
      />
      <input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Title (optional)"
        className="min-w-0 flex-1 rounded border border-border bg-bg px-2 py-1 text-sm focus:border-accent focus:outline-none"
      />
      <Button type="submit" size="sm" disabled={!url.trim() || mut.isPending}>
        {mut.isPending ? 'Adding…' : 'Add'}
      </Button>
    </form>
  )
}

/** Compact count + popover-free affordance for subtasks and calendar events. */
export function AttachmentCount({ parentType, parentId }: { parentType: ParentType; parentId: number }) {
  const q = useAttachments(parentType, parentId)
  const count = q.data?.length ?? 0
  if (count === 0) return null
  return (
    <span className="flex items-center gap-0.5 text-[10px] text-fg-subtle" title={`${count} attachment(s)`}>
      <Paperclip size={10} />
      {count}
    </span>
  )
}
